import { Button, CloseButton, Drawer, Loader, Title } from "@mantine/core";
import { IconFileTypePdf } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { prvApi } from "#/api/prvApi";
import { EmptyState } from "#/components/ui/EmptyState";
import { logger } from "#/utils/logger";
import PrvPatternsPanel, { type PrvPatternsPanelHandle } from "./PrvPatternsPanel";
import { usePrvTranslation } from "./usePrvTranslation";

interface Props {
  clientId: number;
  opened: boolean;
  onClose: () => void;
}

/**
 * TMA-admin results drawer: the engine's output for the whole client. Opens
 * after a manual run completes or from "View patterns". No pass/fail anywhere —
 * that is a later ticket.
 */
const PrvResultsDrawer = ({ clientId, opened, onClose }: Props) => {
  const t = usePrvTranslation();
  const panelRef = useRef<PrvPatternsPanelHandle>(null);
  const [exporting, setExporting] = useState(false);

  const {
    data: view,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["prv-patterns", clientId],
    queryFn: async () => (await prvApi.getPatterns(clientId)).data,
    enabled: opened,
    staleTime: 0,
  });

  const handleExportPdf = async () => {
    if (!panelRef.current || exporting) return;
    setExporting(true);
    try {
      await panelRef.current.exportToPdf(
        `prv-patterns-client-${clientId}-${new Date().toISOString().split("T")[0]}.pdf`
      );
    } catch (error) {
      logger.error("PrvResultsDrawer exportPdf error:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size="90%" withCloseButton={false}>
      <div className="flex h-[calc(100dvh-2rem)] w-full flex-col gap-4 overflow-auto">
        <div className="flex items-start justify-between gap-4 border-b border-b-slate-300 pb-4">
          <div className="flex flex-col gap-1">
            <Title order={3}>{t("clients.prv.results.title")}</Title>
            <span className="text-sm text-gray-500">{t("clients.prv.results.subtitle")}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              leftSection={<IconFileTypePdf size={18} />}
              onClick={() => void handleExportPdf()}
              loading={exporting}
              disabled={!view || view.patterns.length === 0}
            >
              {exporting ? t("common.exporting") : t("common.exportPdf")}
            </Button>
            <div className="h-8 w-px bg-gray-300" />
            <CloseButton onClick={onClose} />
          </div>
        </div>

        {isLoading && (
          <div className="flex h-40 items-center justify-center">
            <Loader color="var(--client-color)" />
          </div>
        )}
        {isError && <EmptyState title={t("common.failedToProcess")} />}

        {/* Remount per open so filters start fresh each time. */}
        {view && opened && <PrvPatternsPanel ref={panelRef} view={view} showRunSummary />}
      </div>
    </Drawer>
  );
};

export default PrvResultsDrawer;
